import { ToggleButton } from "@components/atoms/ToggleButton";
import { useShowCurrencyLabel } from "@util/useShowCurrencyLabel";
import CurrencyDuration from "@components/organisms/common/CurrencyDuration";
import {
  viewport,
  colors,
  font,
} from "../../../styles/cssinjs/helpers/variables.js";
import Link from "next/link";

export default function PremiumPlanComparision({
  plans,
  toggle,
  locale,
  initialPlanType,
  showCurrencyCode = true,
}) {
  const isAnnualBillingPlan = initialPlanType === "annual-billing-plan";
  const isMonthlyBillingPlan = initialPlanType === "monthly-billing-plan";
  const appMode = process.env.APP_MODE;
  const showCurrencyCodeLabel = useShowCurrencyLabel(showCurrencyCode);
  const Card = ({ data, index }) => {
    let cardContainerClassName;
    if (index == 0) {
      cardContainerClassName = "lg:pl-3 lg:pr-3 abs-img-custom-one lg:mr-2";
    } else if (index == 1) {
      cardContainerClassName = "lg:pl-3 lg:pr-3 lg:pb-5 abs-img-custom-two";
    }
    const Costs = ({ data }) =>
      data.map((cost, index) => <span key={index}>{cost}</span>);
    const Header = () => {
      const hasMultiplePrices = Array.isArray(data.price);
      return (
        <>
          <div className="fx  baseline">
            <h2 className="highlight">{data.heading}</h2>
            {data.hasOwnProperty("headingIcon") ? (
              <figure dangerouslySetInnerHTML={{ __html: data.headingIcon }} />
            ) : null}
          </div>
          <p className="plan-user text-light-grayish-blue">{data.label}</p>
          {data.hasOwnProperty("discount") ? (
            <figure
              className={`abs--img discount-img ${
                isAnnualBillingPlan ? "active" : ""
              }`}
              id="monthly-dis-img-cta"
              dangerouslySetInnerHTML={{ __html: data.discount }}
            />
          ) : null}
          {data.hasOwnProperty("discountMonthly") ? (
            <figure
              className="abs--img monthly-billing-span md:d-none"
              id="annual-dis-img-cta"
              dangerouslySetInnerHTML={{ __html: data.discountMonthly }}
            />
          ) : null}
          <p className="plan-price mb-2">
            <strong
              className={`highlight ${isAnnualBillingPlan ? "counter" : ""} ${
                hasMultiplePrices ? "price-wrapper" : ""
              }`}
            >
              ${hasMultiplePrices ? <Costs data={data.price} /> : data.price}
            </strong>{" "}
            <span
              className={`plan-duration-wrap text-light-grayish-blue ${
                showCurrencyCodeLabel ? "has-currency-code" : ""
              }`}
            >
              <CurrencyDuration
                show={showCurrencyCodeLabel}
                duration={data.duration}
              />
            </span>
          </p>
          <style jsx>{`
            .baseline {
              align-items: baseline;
            }
            .fx--sb > figure {
              opacity: 1;
            }
            figure {
              opacity: 0;
            }
            figure.active {
              opacity: 1;
            }
            .highlight + figure {
              opacity: 1;
              margin-left: 8px;
            }
            .abs--img {
              top: 90px;
              left: 84px;
              position: absolute;
              transition-property: opacity;
              transition-duration: 0.5s;
            }
            #dis-img-toggle-cta {
              left: 140px;
              top: -50px;
              position: relative;
              display: inline-block;
              opacity: 0;
            }
            #dis-img-toggle-cta.abs--img {
              opacity: 0;
            }
            #dis-img-toggle-cta.active {
              opacity: 1;
            }
            #annual-dis-img-cta.abs--img,
            #annual-dis-img.abs--img,
            #annual-pro-dis-img.abs--img,
            #dis-img-toggle.abs--img,
            #monthly-dis-img-cta.abs--img,
            #monthly-dis-img.abs--img {
              opacity: 0;
            }
            #annual-dis-img-cta.active,
            #annual-dis-img.active,
            #annual-pro-dis-img.active,
            #dis-img-toggle.active,
            #monthly-dis-img-cta.active,
            #monthly-dis-img.active {
              opacity: 1;
            }
            .billing-toggle {
              margin-left: 38%;
              margin-right: -25px;
            }
            @media (max-width: ${viewport.tablet.sm}) {
              #dis-img-toggle-cta {
                left: 78px;
              }
              .abs--img {
                left: 66px;
              }
            }
          `}</style>
        </>
      );
    };
    const Features = () => (
      <div className="mt-2 md:mb-2 plan-benefits">
        <ul className="ticked-bulletin mb-0 mt-0">
          {data.features.map((feature, index) => (
            <li
              className="text-grey-800"
              key={index}
              dangerouslySetInnerHTML={{ __html: feature }}
            />
          ))}
        </ul>

        {data.learnMore ? (
          <div className="pro-learn-more mt-2">
            <Link href={data.learnMore.href}>{data.learnMore.content}</Link>
          </div>
        ) : null}
        <style jsx>
          {`
            .pro-learn-more {
              color: ${colors.primitive.green["light"]};
              cursor: pointer;
            }
            .ticked-bulletin li {
              margin-bottom: 0px !important;
            }

            .ticked-bulletin li:last-child::before {
              ${index === 0
                ? `
              background: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%23fff' d='M0 0h20v20H0z'/%3E%3Cpath d='M16 15H4l6-6 6 6zM16 9H4l6-6 6 6z' stroke='%23556270' stroke-width='1.1' stroke-linejoin='round'/%3E%3C/svg%3E")
                no-repeat;
              top: 5px;
              `
                : ``}
            }
          `}
        </style>
      </div>
    );
    const ToggleBtn = () => (
      <div className="billing-type-wrap billing-toggle-mobile-view lg:d-none md:mt-2">
        <div className="togg-btn-wrap">
          <ToggleButton toggled={isAnnualBillingPlan} />
        </div>
        <span className="monthly-billing-span">
          {data.offer.length > 1 ? (
            <>
              {data.offer}
              <br />
            </>
          ) : null}
          <span className="md:d-block w-auto">{data.billingType}</span>
        </span>
        <span
          className={`fw-700 annual-billing-span ${
            isAnnualBillingPlan ? "active" : ""
          }`}
        >
          {data.billingType}
        </span>
        <style jsx>{`
          .togg-btn-wrap {
            display: inline-block;
            vertical-align: middle;
            margin: 0 12px 0 16px;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .togg-btn-wrap {
              margin: 0 12px 0 0px !important;
            }
            .billing-toggle-mobile-view .togg-btn-wrap .g-togg--on {
              background-color: ${colors.primitive.green[200]};
            }
            .billing-toggle-mobile-view .togg-btn-wrap .g-togg--off {
              background-color: ${colors.primitive.black[300]};
            }
            .billing-toggle-mobile-view .annual-billing-span {
              display: none;
            }
            .billing-toggle-mobile-view .annual-billing-span.active,
            .billing-toggle-mobile-view .monthly-billing-span.active {
              display: inline-block;
              width: auto;
              vertical-align: middle;
            }
            .billing-toggle-mobile-view .monthly-billing-span {
              font-size: ${font.size.sm[3]};
              line-height: ${font.size.sm[4]};
              font-weight: ${font.weight.semiBold};
              color: ${colors.primitive.green[200]};
              display: none;
            }
            .billing-toggle-mobile-view .monthly-billing-span span {
              color: ${colors.primitive.black[400]};
            }
          }
        `}</style>
      </div>
    );
    const CtaButton = () => (
      <div className="plan-cta mt-2">
        <Toggle />
        <a
          href={data.button[0].url[appMode]}
          className={`${data.button[0].variant} w-100`}
        >
          {data.button[0].content}
        </a>
        {data.hasOwnProperty("notice") ? (
          <div className="notice fx">
            <svg
              width="24"
              height="20"
              viewBox="0 0 24 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 4.294 7.875 1m0 0 3.667 6.587M7.875 1C.5 15 12 24.5 23 14"
                stroke="#303A47"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span> {data.notice} </span>
          </div>
        ) : null}
        <style jsx>{`
          .notice {
            text-align: left;
            margin-top: 16px;
          }
        `}</style>
      </div>
    );
    return (
      <>
        <li className={cardContainerClassName}>
          <Header />
          {data.hasOwnProperty("hasToggle") ? <ToggleBtn /> : null}
          <CtaButton />
          <Features />
        </li>
        <style jsx>{`
          li {
            padding: 16px 24px 20px !important;
          }
          .abs-img-custom-one {
            min-width: 360px;
            box-shadow: 0px 3px 6px 0px #303a4714, 0px 10px 20px 0px #303a4726;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .abs-img-custom-one {
              min-width: 100%;
            }
          }
        `}</style>
      </>
    );
  };
  // pro toggle
  const Toggle = () => (
    <div className="billing-type-wrap mb-2 text-left" id="mobile">
      <div className="togg-btn-wrap">
        <ToggleButton toggled={isAnnualBillingPlan} />
      </div>
      <span
        className={`w-auto annual-billing-span ${
          isAnnualBillingPlan ? "active" : ""
        }`}
        id="annual-billing-span-cta"
      >
        {toggle.billingType[1]}
      </span>
      <figure
        className={`abs--img monthly-billing-span ${
          isMonthlyBillingPlan ? "active" : ""
        }`}
        id="dis-img-toggle-cta"
      >
        <svg width="106" height="24">
          <use href="/sprite.svg#fifty-annual"></use>
        </svg>
      </figure>
      <style jsx>{`
        .billing-type-wrap {
          margin-right: auto;
          margin-left: auto;
        }
        .billing-type-wrap span {
          vertical-align: middle;
          display: inline-block;
          text-align: left;
          width: calc(50% - 50px);
          font-size: ${font.size.sm[3]};
        }
        .annual-billing-span.active,
        .monthly-billing-span.active {
          font-weight: ${font.weight.bold};
          color: ${colors.primitive.black[100]};
        }
        .billing-type-wrap span:first-child {
          text-align: right;
        }
        .togg-btn-wrap {
          display: inline-block;
          vertical-align: middle;
          margin: 0 12px 0 16px;
        }
        .togg-btn-wrap {
          margin: 0 12px 0 0px;
        }
        .abs--img {
          top: 90px;
          left: 84px;
          position: absolute;
          transition-property: opacity;
          transition-duration: 0.5s;
        }
        #dis-img-toggle-cta {
          left: -120px;
          top: -20px;
          position: relative;
          display: inline-block;
          opacity: 0;
        }
        #dis-img-toggle-cta.abs--img {
          opacity: 0;
        }
        #dis-img-toggle-cta.active {
          opacity: 1;
        }
        #annual-dis-img-cta.abs--img,
        #annual-dis-img.abs--img,
        #annual-pro-dis-img.abs--img,
        #dis-img-toggle.abs--img,
        #monthly-dis-img-cta.abs--img,
        #monthly-dis-img.abs--img {
          opacity: 0;
        }
        #annual-dis-img-cta.active,
        #annual-dis-img.active,
        #annual-pro-dis-img.active,
        #dis-img-toggle.active,
        #monthly-dis-img-cta.active,
        #monthly-dis-img.active {
          opacity: 1;
        }
        .billing-toggle {
          margin-left: 38%;
          margin-right: -25px;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          #mobile {
            display: none !important;
            max-width: 350px;
            margin-left: 0;
            margin-right: auto;
          }
          .billing-type-wrap#mobile {
            display: block;
          }
          .billing-type-wrap #dis-img-toggle-cta {
            left: -102px;
            top: -18px;
            display: inline-block;
          }
          .abs--img {
            left: 66px;
          }
        }
      `}</style>
    </div>
  );
  return (
    <>
      <ul className="card--default fx--ch pr-card pr-card-toggle pr-card-header has-gutter-md">
        {plans.map((plan, index) => (
          <Card data={plan} key={index} index={index} />
        ))}
      </ul>
    </>
  );
}
